/*
 * File Path: client/src/components/dashboard/Properties/ManageProperties.js
 * Description: Component to display and manage user's properties, separating owned and shared properties.
 * Developed by: M. YOUCEF Yazid (yazid.youcef@gmail.com)
 * BDAO-SIS Project
 * Version: 1.1.0 // Separated owned and shared properties display
 * CreateDate: 2025-05-01
 * UpdateDate: 2025-05-04 // Separated owned and shared properties display
 */

import React, { useState, useEffect, useMemo } from 'react'; // Added useMemo
import { useAuth } from '../../../context/AuthContext.js';
import { useTranslation } from 'react-i18next';
import { FaShareAltSquare, FaShareSquare, FaPlus, FaEdit, FaShareAlt, FaHome, FaTree, FaWarehouse, FaCar, FaBuilding, FaQuestionCircle, FaTractor, FaStore } from 'react-icons/fa';
import LoadingSpinner from '../../common/LoadingSpinner.js';
import AddPropertyForm from './AddPropertyForm.js';
import EditPropertyForm from './EditPropertyForm.js';
import SharePropertyModal from './SharePropertyModal.js';
import ViewSharedProperty from './ViewSharedProperty.js';
import { fetchProperties } from '../../../api/authApi';

import './ManageProperties.css'; // Import CSS

// --- Icon Mapping ---
const propertyIcons = {
    house: FaHome,
    apartment: FaBuilding,
    room: FaBuilding,
    garden: FaTree,
    garage: FaCar,
    farm: FaTractor,
    field: FaTractor,
    barn: FaWarehouse,
    greenhouse: FaWarehouse,
    office: FaBuilding,
    shop: FaStore,
    warehouse: FaWarehouse,
    factory: FaWarehouse,
    workshop: FaWarehouse,
    pool_area: FaQuestionCircle,
    other: FaQuestionCircle,
    // Add more mappings as needed
};
const DefaultIcon = FaQuestionCircle;
// --- End Icon Mapping ---

function ManageProperties() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [propertiesList, setPropertiesList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingPropertyData, setEditingPropertyData] = useState(null);
    const [showShareModal, setShowShareModal] = useState(false);
    const [sharingProperty, setSharingProperty] = useState(null);
    const [viewingSharedPropertyData, setViewingSharedPropertyData] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Function to trigger refetch
    const triggerRefetch = () => setRefreshTrigger(prev => prev + 1);

    // Fetch properties on mount and when refreshTrigger changes
    useEffect(() => {
        let isMounted = true;
        const loadProperties = async () => {
            setLoading(true); setError(null);
            try {
                console.log("[ManageProperties] Fetching properties...");
                const combinedList = await fetchProperties();
                console.log("[ManageProperties] Properties received:", combinedList);
                if (isMounted) {
                    setPropertiesList(combinedList || []);
                }
            } catch (err) {
                console.error("Error loading properties:", err);
                if (isMounted) setError(err.message || t('errors.api.requestFailed'));
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        // Only fetch if no sub-view is active
        if (!editingPropertyData && !showAddForm && !viewingSharedPropertyData && !showShareModal) {
            loadProperties();
        };
        return () => { isMounted = false };
    }, [t, refreshTrigger, editingPropertyData, showAddForm, viewingSharedPropertyData, showShareModal]);

    // --- Filter properties into owned and shared using useMemo ---
    const { ownedProperties, sharedProperties } = useMemo(() => {
        const owned = propertiesList.filter(prop => prop.owned_by_user);
        const shared = propertiesList.filter(prop => !prop.owned_by_user);
        // Optional: Sort each list alphabetically by name here if needed
        owned.sort((a, b) => a.property_name.localeCompare(b.property_name));
        shared.sort((a, b) => a.property_name.localeCompare(b.property_name));
        return { ownedProperties: owned, sharedProperties: shared };
    }, [propertiesList]);
    // --- End Filtering ---

    const clearSubViews = () => {
        setShowAddForm(false);
        setEditingPropertyData(null);
        setShowShareModal(false);
        setSharingProperty(null);
        setViewingSharedPropertyData(null);
        setError(null);
    };

    const toggleAddForm = () => {
        clearSubViews(); // Close other views when opening add form
        setShowAddForm(prev => !prev);
    };

    const handleAddSuccess = () => {
        console.log("[ManageProperties] handleAddSuccess called");
        clearSubViews();
        triggerRefetch();
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleAddForm();
        }
    };

    const handleEdit = (property) => {
        clearSubViews();
        console.log("Edit clicked for property:", property);
        setEditingPropertyData(property);
    };

    const handleEditCancel = () => {
        clearSubViews();
    };

    const handleUpdateSuccess = () => {
        clearSubViews();
        console.log("Property updated successfully");
        triggerRefetch();
    };

    const handleDeleteSuccess = () => {
        clearSubViews();
        console.log("Property deleted successfully");
        triggerRefetch();
    };

    const handleShare = (property) => {
        clearSubViews();
        console.log("Share clicked for property:", property);
        setSharingProperty(property);
        setShowShareModal(true);
    };

    const handleViewShared = (property) => {
        clearSubViews();
        setViewingSharedPropertyData(property);
    };

    const handleViewCancel = () => {
        clearSubViews();
    };

    const handleLeaveShareSuccess = () => {
        clearSubViews();
        triggerRefetch();
    };

    const closeShareModal = () => {
        setShowShareModal(false);
        setSharingProperty(null);
    }

    // Determine if the main grid section should be shown
    const shouldShowGrid = !showAddForm && !editingPropertyData && !viewingSharedPropertyData;

    if (!user) { // Check if user context is available
        return <LoadingSpinner />;
    }

    return (
        <div className="properties-container">
            {/* Main Title */}
            <div className="properties-title-container">
                <h1>{t('dashboard.properties.title', 'Manage Properties')}</h1>
            </div>

            {/* General Error Display (Only show if not in a sub-view) */}
            {error && shouldShowGrid && (
                 <div className="form-error">{error}</div>
             )}

            {/* --- Conditional Rendering Section --- */}

            {/* 1. Show Add Form */}
            {showAddForm && (
                <div className="add-property-form-container">
                    <AddPropertyForm onSuccess={handleAddSuccess} onCancel={toggleAddForm} existingProperties={ownedProperties} />
                </div>
            )}

            {/* 2. Show Edit Form */}
            {editingPropertyData && (
                <div className="edit-property-form-container">
                    <EditPropertyForm
                        propertyData={editingPropertyData}
                        onUpdateSuccess={handleUpdateSuccess}
                        onDeleteSuccess={handleDeleteSuccess}
                        onCancel={handleEditCancel}
                    />
                </div>
            )}

            {/* 3. Show Shared Property View */}
            {viewingSharedPropertyData && (
                 <div className="view-property-form-container">
                     <ViewSharedProperty
                         propertyData={viewingSharedPropertyData}
                         onLeaveSuccess={handleLeaveShareSuccess}
                         onCancel={handleViewCancel}
                     />
                 </div>
             )}

            {/* 4. Show Property Grids if NOT Adding, Editing, or Viewing Shared Details */}
            {shouldShowGrid && (
                <>
                    {/* Owned Properties Section */}
                    <div className="properties-section owned-properties"> {/* Added specific class */}
                        <h2>{t('dashboard.properties.myPropertiesTitle', 'My Properties')}</h2>
                        {loading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                                <LoadingSpinner size="medium"/>
                            </div>
                        ) : ownedProperties.length === 0 ? (
                             <p className="no-properties-message">{t('dashboard.properties.noOwnedProperties', 'You haven\'t added any properties yet.')}</p>
                        ) : (
                            <div className="properties-grid">
                                {/* Map over owned properties */}
                                {ownedProperties.map(prop => {
                                    const IconComponent = propertyIcons[prop.property_type] || DefaultIcon;
                                    const isSharedByOwner = prop.is_shared_by_owner; // Flag from API
                                    return (
                                        <div
                                            key={prop.property_id}
                                            className="property-card" // Standard card for owned
                                        >
                                            {/* Action Icons */}
                                            <div className="property-card-actions">
                                                <button onClick={(e) => { e.stopPropagation(); handleShare(prop); }} title={t('properties.share', 'Share')}> <FaShareAlt /> </button>
                                                <button onClick={(e) => { e.stopPropagation(); handleEdit(prop); }} title={t('common.edit', 'Edit')}> <FaEdit /> </button>
                                            </div>
                                            {/* Shared Out Indicator */}
                                            {isSharedByOwner && (
                                                <div className="shared-out-indicator" title={t('properties.sharedByYou', 'Shared by you')}>
                                                    <FaShareSquare />
                                                </div>
                                            )}
                                            {/* Main Card Content */}
                                            <IconComponent className="property-icon" />
                                            <p className="property-name">{prop.property_name}</p>
                                        </div>
                                    );
                                })}
                                {/* Add New Property Button */}
                                <div className="add-property-container"
                                    onClick={toggleAddForm}
                                    onKeyPress={handleKeyPress}
                                    role="button" tabIndex={0}
                                >
                                    <div
                                        className="add-property-button"
                                        aria-label={t('properties.add', 'Add New Property')}
                                    >
                                        <FaPlus />
                                    </div>
                                    {/* Optional: Add text label below the button */}
                                    {/* <p className="add-property-label">{t('properties.add', 'Add New Property')}</p> */}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Shared Properties Section */}
                    {sharedProperties.length > 0 && ( // Only show section if there are shared properties
                        <div className="properties-section shared-properties"> {/* Added specific class */}
                            <h2>{t('dashboard.properties.sharedWithMeTitle', 'Shared with Me')}</h2>
                            {loading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                                    <LoadingSpinner size="medium"/>
                                </div>
                            ) : (
                                <div className="properties-grid">
                                    {/* Map over shared properties */}
                                    {sharedProperties.map(prop => {
                                        const IconComponent = propertyIcons[prop.property_type] || DefaultIcon;
                                        return (
                                            <div
                                                key={prop.property_id}
                                                className="property-card shared-property-card" // Specific class for shared
                                                onClick={() => handleViewShared(prop)} // Click to view details
                                                tabIndex={0} // Make it focusable
                                                onKeyPress={(e) => { if(e.key === 'Enter' || e.key === ' ') handleViewShared(prop); }}
                                                role="button" // Semantics
                                            >
                                                {/* Shared In Indicator */}
                                                <div className="shared-in-indicator" title={t('properties.sharedWithYou', 'Shared with you')}>
                                                    <FaShareAltSquare />
                                                </div>
                                                {/* Main Card Content */}
                                                <IconComponent className="property-icon" />
                                                <p className="property-name">{prop.property_name}</p>
                                                {/* Display Owner Username */}
                                                {prop.owner_username && (
                                                    <p className="shared-owner-text">({t('properties.sharedBy', 'Shared by')}: {prop.owner_username})</p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                    {/* Render copyright only when grid is shown */}
                    <div className="dashboard-footer-copyright">
                         &copy; {new Date().getFullYear()} beinSmart.cloud. {t('footer.allRightsReserved')}
                    </div>
                </>
            )}
            {/* --- End Conditional Rendering --- */}

            {/* Share Modal */}
            {showShareModal && sharingProperty && (
                <SharePropertyModal
                    property={sharingProperty}
                    onClose={closeShareModal}
                />
            )}
        </div> // End properties-container
    );
}

export default ManageProperties;
